import { defineStore } from 'pinia';
import apolloClient from 'utils/apolloClient';
import Vue from 'vue';
import {
  INITIAL_STATE_BOX,
  INITIAL_STATE_ACTIVITIES,
  INITIAL_STATE_TRAVEL_UY,
} from 'constants/initialState';
import keywordTranslate from 'utils/keywordTranslate';
import ElasticPaginatedBoxes from 'assets/graphql/queries/ElasticPaginatedBoxes.graphql';
import ElasticPaginatedActivities from 'assets/graphql/queries/ElasticPaginatedActivities.graphql';
import ElasticPaginatedTravelUyActivities from 'assets/graphql/queries/ElasticPaginatedTravelUyActivities.graphql';

import {
  hasValue,
  createTagsForFacet,
  processPriceFilter,
  hasValidFilters,
  urlCallbackManager,
} from 'utils/filterHelpers';

const FILTER_TYPES = {
  BOX: 0,
  ACTIVITY: 1,
  TRAVEL_UY: 2,
};

const SHARED_FILTERS = ['province', 'category_name'];

const QUERY_CONFIGS = [
  {
    query: ElasticPaginatedBoxes,
    paginatedName: 'elastic_paginated_boxes',
    initialState: INITIAL_STATE_BOX,
  },
  {
    query: ElasticPaginatedActivities,
    paginatedName: 'elastic_paginated_activities',
    initialState: INITIAL_STATE_ACTIVITIES,
  },
  {
    query: ElasticPaginatedTravelUyActivities,
    paginatedName: 'elastic_paginated_travel_activities_uy',
    initialState: INITIAL_STATE_TRAVEL_UY,
  },
];

export const useFiltersStore = defineStore('filters', {
  state: () => ({
    currentFilters: QUERY_CONFIGS.map(config => ({ ...config.initialState })),
    facets: [],
    results: [],
    page_info: { number: 1 },
    allResultsCount: { 0: 0, 1: 0, 2: 0 },
    loading: false,
    optionCards: [
      {
        name: 'box',
        title: 'Regalos con opciones',
        retailTitle: 'Boxes',
        description: `${keywordTranslate(
          'Regalá',
        )} un <b>conjunto de experiencias</b>, tu agasajado elige cuál vive.`,
      },
      {
        name: 'activity',
        title: 'Actividades',
        retailTitle: 'Experiencias',
        description: `${keywordTranslate('Regalá')} o ${keywordTranslate(
          'disfrutá',
        )} de <b>una actividad en especial.</b>`,
      },
      {
        name: 'travelUy',
        title: 'Viajes',
        description: `${keywordTranslate(
          'Disfrutá',
        )} de <b>alojamientos y experiencias</b> en los mejores destinos.`,
      },
    ],
    cardSelected: 0,
    orderBy: 'relevance',
    keywords: '',
    boxId: null,
    isInitializing: false,
    shouldUpdateURL: true,
    // Request management for race condition prevention
    activeRequestController: null,
    requestCounter: 0,
    _isInsideLoadingFetch: false,
  }),

  getters: {
    getFacets: state => state.facets,
    getCurrentFilters: state => state.currentFilters,
    getAllResultsCount: state => state.allResultsCount,
    getOptionCards: state => state.optionCards,
    getCardSelected: state => state.cardSelected,
    getPageInfo: state => state.page_info,
    getResults: state => state.results,
    getLoading: state => state.loading,
    getCurrentOrder: state => state.orderBy,

    getCurrentTags: state => {
      const { currentFilters, facets, cardSelected } = state;
      const currentFilter = currentFilters[cardSelected];

      if (!currentFilter || !facets || facets.length === 0) {
        return [];
      }

      return Object.entries(currentFilter).reduce((tags, [key, value]) => {
        if (!hasValue(value)) {
          return tags;
        }

        const facet = facets.find(f => f.name === key);
        if (!facet) {
          return tags;
        }

        const generatedTags = createTagsForFacet(facet, value, key);
        return [...tags, ...generatedTags];
      }, []);
    },

    sendableFilters: state => {
      const config = QUERY_CONFIGS[state.cardSelected];
      if (!config || !state.currentFilters[state.cardSelected]) {
        return {};
      }

      const filters = { ...state.currentFilters[state.cardSelected] };

      filters.price = processPriceFilter(filters.price);
      filters.keywords = state.keywords;

      if (state.cardSelected !== FILTER_TYPES.ACTIVITY) {
        delete filters.has_online_reservation;
      }

      return filters;
    },
  },

  actions: {
    async init(initialKeywords = null) {
      try {
        this.isInitializing = true;
        this.currentFilters = QUERY_CONFIGS.map(config => ({
          ...config.initialState,
        }));
        this._resetPagination();
        // Solo limpiar keywords si no se pasan como parámetro inicial
        if (initialKeywords !== null) {
          this.keywords = initialKeywords;
        } else {
          this.keywords = '';
        }
        this.orderBy = 'relevance';
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error on filters store init', err);
      } finally {
        this.isInitializing = false;
      }
    },

    async updateFilters(updates = {}) {
      const { key, inputType, value, orderBy: newOrderBy } = updates;

      try {
        this.loading = true;

        if (newOrderBy) {
          this.orderBy = newOrderBy;
        }

        if (key && inputType) {
          this._updateFilterValue(key, inputType, value);
        }

        this._resetPagination();

        await this._fetchCurrentResults();

        if (this._hasValidFilters()) {
          await this._updateAllResultCounts();
        }

        if (!this.isInitializing && this.shouldUpdateURL) {
          this._updateURLFromFilters();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating filters:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async removeTag({ key, inputType, value }) {
      let newValue;

      if (inputType === 'radio' || inputType === 'toggle') {
        newValue = [];
      } else if (inputType === 'checkbox') {
        const currentValues = this.currentFilters[this.cardSelected][key] || [];
        newValue = currentValues.filter(v => v !== value);
      }

      // Usar updateFilters directamente para actualizar, sincronizar y hacer fetch
      await this.updateFilters({
        key,
        inputType,
        value: newValue,
      });
    },

    setKeywords(keywords, shouldFetch = true, shouldUpdateURL = true) {
      this.keywords = keywords;

      if (!this.isInitializing && this.shouldUpdateURL && shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      if (shouldFetch) {
        this._setLoadingAndFetch(() => this._fetchCurrentResults());
      }
    },

    async setBoxCategory(category, shouldFetch = true) {
      Vue.set(
        this.currentFilters[this.cardSelected],
        'category_name',
        category,
      );

      if (shouldFetch) {
        await this._setLoadingAndFetch(() => this._fetchCurrentResults());
      }
    },

    async setCard(cardId, shouldFetch = true) {
      const newCardId = cardId ?? 0;
      if (newCardId !== this.cardSelected) {
        const previousCardId = this.cardSelected;
        this.cardSelected = newCardId;

        this._syncSharedFiltersOnCardChange(previousCardId, newCardId);

        if (shouldFetch) {
          await this._setLoadingAndFetch(() => this._fetchCurrentResults());
        }
      }
    },

    async clearCurrentFilters(options = {}) {
      const { clearSharedFilters = true, refetch = true } = options;
      const sharedFiltersToKeep = {};
      if (!clearSharedFilters) {
        SHARED_FILTERS.forEach(filterKey => {
          const boxValue = this.currentFilters[FILTER_TYPES.BOX][filterKey];
          const activityValue =
            this.currentFilters[FILTER_TYPES.ACTIVITY][filterKey];

          if (boxValue && boxValue.length > 0) {
            // Copiar correctamente arrays y strings
            sharedFiltersToKeep[filterKey] = Array.isArray(boxValue)
              ? [...boxValue]
              : boxValue;
          } else if (activityValue && activityValue.length > 0) {
            // Copiar correctamente arrays y strings
            sharedFiltersToKeep[filterKey] = Array.isArray(activityValue)
              ? [...activityValue]
              : activityValue;
          }
        });
      }

      this._cancelActiveRequest();

      this.currentFilters = QUERY_CONFIGS.map(config => ({
        ...config.initialState,
      }));

      if (!clearSharedFilters && Object.keys(sharedFiltersToKeep).length > 0) {
        Object.entries(sharedFiltersToKeep).forEach(([filterKey, value]) => {
          Vue.set(this.currentFilters[FILTER_TYPES.BOX], filterKey, value);
          Vue.set(this.currentFilters[FILTER_TYPES.ACTIVITY], filterKey, value);
        });
      }

      this.orderBy = 'relevance';
      this._resetPagination();

      if (!this.isInitializing && this.shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      if (refetch) {
        try {
          this.loading = true;
          await this._fetchCurrentResults();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            'Error fetching results after clearing filters:',
            error,
          );
          throw error;
        } finally {
          this.loading = false;
        }
      }
    },

    async updatePageNumber(pageNumber) {
      this.page_info.number = pageNumber;
      await this._setLoadingAndFetch(() => this._fetchCurrentResults());
    },

    resetPaginationNumber() {
      this._resetPagination();
    },

    setBoxId(boxId, shouldFetch = true, shouldUpdateURL = true) {
      this.boxId = boxId;

      if (!this.isInitializing && this.shouldUpdateURL && shouldUpdateURL) {
        this._updateURLFromFilters();
      }

      if (shouldFetch) {
        this._setLoadingAndFetch(() => this._fetchCurrentResults());
      }
    },

    _updateFilterValue(key, inputType, value) {
      this._setFilterValue(key, value);

      if (SHARED_FILTERS.includes(key)) {
        this._syncSharedFilters(key, value);
      }
    },

    _setFilterValue(key, value) {
      Vue.set(this.currentFilters[this.cardSelected], key, value);
    },

    _syncSharedFilters(key, value) {
      const targetTypes = [FILTER_TYPES.BOX, FILTER_TYPES.ACTIVITY];

      targetTypes.forEach(filterType => {
        if (filterType !== this.cardSelected) {
          Vue.set(this.currentFilters[filterType], key, value);
        }
      });
    },

    _syncSharedFiltersOnCardChange(previousCardId, newCardId) {
      const isBoxToActivity =
        previousCardId === FILTER_TYPES.BOX &&
        newCardId === FILTER_TYPES.ACTIVITY;
      const isActivityToBox =
        previousCardId === FILTER_TYPES.ACTIVITY &&
        newCardId === FILTER_TYPES.BOX;

      if (isBoxToActivity || isActivityToBox) {
        const sourceFilters = this.currentFilters[previousCardId];

        SHARED_FILTERS.forEach(filterKey => {
          const sourceValue = sourceFilters[filterKey];
          let value;

          if (sourceValue && sourceValue.length > 0) {
            value = Array.isArray(sourceValue) ? [...sourceValue] : sourceValue;
          } else {
            const config = QUERY_CONFIGS[newCardId];
            value = config.initialState[filterKey];
          }

          Vue.set(this.currentFilters[newCardId], filterKey, value);
        });
      }
    },

    _resetPagination() {
      this.page_info.number = 1;
    },

    _hasValidFilters() {
      return hasValidFilters(this.sendableFilters);
    },

    async _fetchCurrentResults() {
      this._cancelActiveRequest();
      this.activeRequestController = new AbortController();
      const requestId = ++this.requestCounter;

      const config = QUERY_CONFIGS[this.cardSelected];
      const variables = {
        filters: JSON.stringify(this.sendableFilters),
        order_by: this.orderBy,
        page_number: this.page_info.number,
        per_page: 12,
        view: this.cardSelected === 3 ? 'box_catalog' : '',
      };

      try {
        const { data } = await apolloClient.query({
          query: config.query,
          variables,
          fetchPolicy: 'no-cache',
          context: {
            fetchOptions: {
              signal: this.activeRequestController.signal,
            },
          },
        });
        if (
          requestId === this.requestCounter &&
          !this.activeRequestController.signal.aborted
        ) {
          const result = data[config.paginatedName];
          this.results = result.results;
          this.facets = result.facets;
          this.page_info = result.page_info;
          this.allResultsCount[this.cardSelected] = result.page_info.count;

          this._updateDynamicSubfilters();
        }
      } catch (error) {
        if (
          error.name !== 'AbortError' &&
          !this.activeRequestController?.signal?.aborted
        ) {
          throw error;
        }
      } finally {
        if (requestId === this.requestCounter) {
          this.activeRequestController = null;
          this._updateURLFromFilters();
        }
      }
    },

    _updateDynamicSubfilters() {
      this.facets.forEach(facet => {
        if (facet.name.includes('subfilter_')) {
          const currentValue =
            this.currentFilters[this.cardSelected][facet.name] || [];
          Vue.set(
            this.currentFilters[this.cardSelected],
            facet.name,
            currentValue,
          );
        }
      });
    },

    async _updateAllResultCounts() {
      const promises = Object.keys(this.allResultsCount)
        .filter(index => Number(index) !== this.cardSelected)
        .map(async index => {
          const config = QUERY_CONFIGS[index];

          const filters = { ...this.currentFilters[index] };
          filters.price = processPriceFilter(filters.price);
          filters.keywords = this.keywords;

          if (Number(index) !== FILTER_TYPES.ACTIVITY) {
            delete filters.has_online_reservation;
          }

          const variables = {
            filters: JSON.stringify(filters),
            order_by: this.orderBy,
            page_number: 1,
            per_page: 12,
          };

          const { data } = await apolloClient.query({
            query: config.query,
            variables,
          });

          this.allResultsCount[index] =
            data[config.paginatedName].page_info.count;
        });

      await Promise.all(promises);
    },

    async _setLoadingAndFetch(fetchFunction, forceExecution = false) {
      if (this._isInsideLoadingFetch && !forceExecution) {
        return await fetchFunction();
      }

      if (!forceExecution && this.loading) {
        return;
      }

      if (forceExecution) {
        this._cancelActiveRequest();
        this._isInsideLoadingFetch = false;
        this.loading = false;
      } else if (this.loading) {
        return;
      }

      try {
        this._isInsideLoadingFetch = true;
        this.loading = true;
        const result = await fetchFunction();
        return result;
      } catch (error) {
        if (error.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Error in _setLoadingAndFetch:', error);
          throw error;
        }
      } finally {
        this._isInsideLoadingFetch = false;
        this.loading = false;
      }
    },

    async handleChangeFilters(key, type) {
      if (type === 'orderBy') {
        await this.updateFilters({ orderBy: key });
      } else {
        await this.updateFilters({ key, inputType: type });
      }
    },

    async getDataFromFilters() {
      if (this._isInsideLoadingFetch) {
        return await this._fetchCurrentResults();
      }
      return await this._setLoadingAndFetch(() => this._fetchCurrentResults());
    },

    async getTravel() {
      this.cardSelected = FILTER_TYPES.TRAVEL_UY;
      await this.updateFilters();
      return this.results;
    },

    async getBoxes() {
      this.cardSelected = FILTER_TYPES.BOX;
      await this.updateFilters();
      return this.results;
    },

    async getActivities() {
      this.cardSelected = FILTER_TYPES.ACTIVITY;
      await this.updateFilters();
      return this.results;
    },

    deleteTags(tagInfo) {
      return this.removeTag(tagInfo);
    },

    applyURLFilters(urlFilters) {
      this.shouldUpdateURL = false;
      const isUrlFiltersArray =
        Array.isArray(urlFilters) && urlFilters.length > 0;
      try {
        if (isUrlFiltersArray) {
          urlFilters.forEach((filterSet, index) => {
            if (filterSet && typeof filterSet === 'object') {
              const config = QUERY_CONFIGS[index];
              const result = { ...config?.initialState, ...filterSet };
              Vue.set(this.currentFilters, index, result);
              Object.keys(filterSet).forEach(filterKey => {
                Vue.set(
                  this.currentFilters[index],
                  filterKey,
                  filterSet[filterKey],
                );
              });
            } else {
              Vue.set(this.currentFilters, index, {
                ...QUERY_CONFIGS[index]?.initialState,
              });
            }
          });
        }
      } finally {
        this.shouldUpdateURL = true;
      }
    },

    _updateURLFromFilters() {
      urlCallbackManager.markFiltersURLUpdateNeeded(
        this.currentFilters,
        this.cardSelected,
      );
    },

    registerURLUpdateCallback(callback) {
      urlCallbackManager.register(callback, 'filters');
    },

    unregisterURLUpdateCallback() {
      urlCallbackManager.unregister('filters');
    },

    _cancelActiveRequest() {
      if (this.activeRequestController) {
        // this.activeRequestController.abort();
        this.activeRequestController = null;
        // Ensure loading state is reset when cancelling
        if (this.loading) {
          this.loading = false;
        }
      }
    },

    // Public method to cancel requests when component unmounts or routes change
    cancelAllRequests() {
      this._cancelActiveRequest();
      this.requestCounter = 0;
      this.loading = false;
    },

    /**
     * Genera la query de filtros para una categoría específica
     * @param {number} cardType - Tipo de tarjeta (0: box, 1: activity, 2: travel)
     * @param {string} categoryName - Nombre de la categoría
     * @returns {object} Objeto con card y filters para la query
     */
    getFiltersQueryForCategory(cardType, categoryName) {
      const filters = [
        { ...INITIAL_STATE_BOX }, // BOX
        { ...INITIAL_STATE_ACTIVITIES }, // ACTIVITY
        { ...INITIAL_STATE_TRAVEL_UY }, // TRAVEL_UY
      ];

      if (categoryName) {
        filters[cardType].category_name = categoryName;
      }

      return {
        card: cardType,
        currentFilters: JSON.stringify(filters),
      };
    },
  },
});
